// The UI languages the app ships. Everything locale-typed derives from this
// list, so adding a language here is what makes tsc demand its translations.
export const supportedLocales = ["en", "ru"] as const;

export type Locale = (typeof supportedLocales)[number];

const isLocale = (value: unknown): value is Locale =>
  typeof value === "string" &&
  (supportedLocales as readonly string[]).includes(value);

// The given language, or English when it is not one we ship. Defaults to
// the browser language.
export const getSupportedLocale = (
  locale: string | undefined = navigator.language.slice(0, 2),
): Locale => {
  const lower = locale?.toLowerCase();
  return isLocale(lower) ? lower : "en";
};

// A language picked on LanguageToggle. Boot prefers it over the browser
// language; a signed-in profile's locale beats both (App.tsx).
const STORED_LOCALE_KEY = "ui-locale";

export const readStoredLocale = (): Locale | undefined => {
  try {
    const stored = localStorage.getItem(STORED_LOCALE_KEY);
    return isLocale(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
};

// Only from an explicit user choice, never from i18next's "languageChanged":
// init() fires that with "en" on every boot and would overwrite the choice.
export const storeLocale = (locale: Locale) => {
  try {
    localStorage.setItem(STORED_LOCALE_KEY, locale);
  } catch {
    // Storage blocked (private mode, cleared site data): the choice lasts
    // until the next reload.
  }
};
